import { forwardRef, useMemo } from 'react'
import { cn } from '../../../lib/cn'
import { ChartLegend } from './ChartLegend'
import { EChart, type EChartHandle } from './EChart'
import { formatShare, isFiniteNumber } from './format'
import {
  buildStackedShareOption,
  rowShares,
  segmentColor,
  stackedShareHeight,
  type StackedShareInput,
  type StackedShareRow,
  type StackedShareSegment,
} from './stackedShareOption'
import { useChartTheme } from './useChartTheme'

export interface StackedShareBarsProps extends StackedShareInput {
  height?: number
  loading?: boolean
  ariaLabel?: string
  className?: string
  texture?: boolean
  /** Скрыть HTML-легенду частей (если страница показывает свою). */
  hideLegend?: boolean
  /** Клик по полосе или подписи строки — обычно провал по объекту строки. */
  onRowClick?: (row: StackedShareRow) => void
  /** Клик по сегменту — объект строки и часть (например, «падающие» артикулы темы). */
  onSegmentClick?: (row: StackedShareRow, segment: StackedShareSegment) => void
}

/**
 * Полосы 100 %: структура каждой строки по частям (жизненный цикл макетов темы, доли кабинетов в продажах
 * макета). Легенда — HTML над графиком; ≥ 4 частей — подписи долей внутри сегментов, если влезают, и
 * табличный вид в `ChartCard`.
 */
export const StackedShareBars = forwardRef<EChartHandle, StackedShareBarsProps>(function StackedShareBars(
  { height, loading, ariaLabel, className, texture, hideLegend, onRowClick, onSegmentClick, ...input },
  ref,
) {
  const theme = useChartTheme()
  const option = useMemo(
    () => buildStackedShareOption(input, theme),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [theme, input.rows, input.segments, input.valueFormat, input.labelMinShare, input.rowLabelWidth],
  )
  const legend = input.segments.map((segment, index) => ({ label: segment.label, color: segmentColor(theme, segment, index) }))

  return (
    <div className={className}>
      {!hideLegend && <ChartLegend items={legend} className="mb-2" />}
      <EChart
        ref={ref}
        option={option}
        height={height ?? stackedShareHeight(input.rows.length)}
        loading={loading}
        ariaLabel={ariaLabel}
        texture={texture}
        onMarkClick={(params) => {
          if (params.componentType === 'yAxis') {
            const key = String(params.value ?? '')
            const row = key.startsWith('r') ? input.rows[Number(key.slice(1))] : undefined
            if (row) onRowClick?.(row)
            return
          }
          if (params.componentType !== 'series' || params.dataIndex === undefined) return
          const row = input.rows[params.dataIndex]
          const segment = params.seriesIndex !== undefined ? input.segments[params.seriesIndex] : undefined
          if (!row) return
          if (segment && onSegmentClick) onSegmentClick(row, segment)
          else onRowClick?.(row)
        }}
      />
    </div>
  )
})

export interface ShareBarItem {
  key: string
  label: string
  value: number | null
  /** Слот палитры (0..7), `null` — «Прочее». По умолчанию — порядковый номер. */
  slot?: number | null
  color?: string
}

export interface ShareBarProps {
  items: ShareBarItem[]
  /** Высота полосы, px (по умолчанию 8). */
  height?: number
  className?: string
  /** Подпись для скринридера; по умолчанию — список «часть — доля». */
  ariaLabel?: string
}

/**
 * Полоса 100 % в строке таблицы (HTML, без движка): те же цвета частей, что у `StackedShareBars`, зазор 2 px
 * между сегментами. Значения и доли — в подсказке (title) и подписи для скринридера.
 */
export function ShareBar({ items, height = 8, className, ariaLabel }: ShareBarProps) {
  const theme = useChartTheme()
  const shares = rowShares(items.map((item) => item.value))
  const parts = items
    .map((item, index) => ({
      item,
      share: shares[index],
      color: segmentColor(theme, { key: item.key, label: item.label, slot: item.slot, color: item.color }, index),
    }))
    .filter((part) => isFiniteNumber(part.share) && part.share > 0)
  const summary = items
    .map((item, index) => `${item.label}: ${isFiniteNumber(shares[index]) ? formatShare(shares[index]) : '—'}`)
    .join(', ')

  if (parts.length === 0) {
    return (
      <div
        className={cn('w-full rounded-sm bg-gray-100 dark:bg-zinc-800', className)}
        style={{ height }}
        role="img"
        aria-label={ariaLabel ?? 'Нет данных'}
        title="Нет данных"
      />
    )
  }

  return (
    <div className={cn('flex w-full gap-[2px]', className)} style={{ height }} role="img" aria-label={ariaLabel ?? summary} title={summary}>
      {parts.map((part, index) => (
        <span
          key={part.item.key}
          className={cn('block h-full', index === 0 && 'rounded-l', index === parts.length - 1 && 'rounded-r')}
          style={{ width: `${(part.share as number) * 100}%`, background: part.color, minWidth: 2 }}
        />
      ))}
    </div>
  )
}

export default StackedShareBars
